/**
 * Rumio Balance - Display widget for user's unified Rumio balance
 * Shows single currency with tier unlock costs
 */

import { Loader2, Plus } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useAuthStore } from '../../../../stores/auth-store';
import { useRumioBalance } from '../hooks/useRumios';
import { UNIFIED_RUMIO_CONFIG, MYSTERY_TIERS } from '../utils/constants';
import type { MysteryTier } from '../types';

interface Props {
  onPurchase?: () => void;
  compact?: boolean;
  showTierCosts?: boolean;
}

export default function RumioBalance({ onPurchase, compact = false, showTierCosts = true }: Props) {
  const { t } = useLanguage();
  const { user } = useAuthStore();
  const { data: balance, isLoading, error } = useRumioBalance();

  if (!user) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-2">
        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !balance) {
    return null;
  }

  if (compact) {
    return (
      <button
        onClick={onPurchase}
        className="flex items-center gap-2 px-3 py-2 bg-amber-50 hover:bg-amber-100 rounded-xl transition-colors border border-amber-200"
      >
        <img src={UNIFIED_RUMIO_CONFIG.iconUrl} alt="" className="w-6 h-6" />
        <span className="font-bold text-amber-700 text-lg">{balance.balance}</span>
        {onPurchase && (
          <Plus className="w-4 h-4 text-amber-600" />
        )}
      </button>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
      {/* Header with Balance */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-900">
          {t('rumioBalance.title', 'Your Rumios')}
        </h3>
      </div>

      {/* Main Balance Display */}
      <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-amber-200 mb-4">
        <img src={UNIFIED_RUMIO_CONFIG.iconUrl} alt="" className="w-12 h-12 mx-auto mb-2" />
        <span className="text-4xl font-bold text-amber-700">{balance.balance}</span>
        <p className="text-sm text-amber-600 mt-1">{UNIFIED_RUMIO_CONFIG.namePlural}</p>
      </div>

      {/* Tier Unlock Costs */}
      {showTierCosts && balance.tier_costs && (
        <div className="space-y-2 mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
            {t('rumioBalance.unlockCosts', 'Unlock Costs')}
          </p>
          <div className="grid grid-cols-3 gap-2">
            {(['super', 'fresh', 'standard'] as MysteryTier[]).map((tier) => {
              const tierConfig = MYSTERY_TIERS[tier];
              const cost = balance.tier_costs[tier];
              const canAfford = balance.balance >= cost;

              return (
                <div
                  key={tier}
                  className={`text-center p-2 rounded-lg border ${
                    canAfford
                      ? 'bg-green-50 border-green-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <span className="text-lg">{tierConfig.icon}</span>
                  <p className="text-xs font-medium text-gray-600 mt-0.5">{tierConfig.name}</p>
                  <p className={`text-sm font-bold flex items-center justify-center gap-1 ${canAfford ? 'text-green-600' : 'text-gray-400'}`}>
                    {cost} <img src={UNIFIED_RUMIO_CONFIG.iconUrl} alt="" className="w-4 h-4 inline-block" />
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Purchase Button */}
      {onPurchase && (
        <button
          onClick={onPurchase}
          className="w-full py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold rounded-xl hover:from-amber-600 hover:to-amber-700 transition-colors flex items-center justify-center gap-2 shadow-sm"
        >
          <Plus className="w-4 h-4" />
          {t('rumioBalance.getRumios', 'Get More Rumios')}
        </button>
      )}
    </div>
  );
}

/**
 * Inline version for use in headers/navbars
 */
export function RumioBalanceInline({ onClick }: { onClick?: () => void }) {
  const { t } = useLanguage();
  const { user } = useAuthStore();
  const { data: balance, isLoading } = useRumioBalance();

  if (!user || isLoading || !balance) return null;

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 rounded-full transition-colors border border-amber-200 group"
      title={t('rumioBalance.yourRumios', 'Your Rumios')}
    >
      <img src={UNIFIED_RUMIO_CONFIG.iconUrl} alt="" className="w-5 h-5" />
      <span className="font-bold text-amber-700">{balance.balance}</span>
      {onClick && (
        <Plus className="w-3.5 h-3.5 text-amber-500 group-hover:text-amber-600 transition-colors" />
      )}
    </button>
  );
}

/**
 * Badge version for showing Rumio count with optional tier context
 */
export function RumioBadge({
  count,
  tier,
  size = 'md',
  showCost = false,
}: {
  count: number;
  tier?: MysteryTier;
  size?: 'sm' | 'md' | 'lg';
  showCost?: boolean;
}) {
  const { t } = useLanguage();
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5',
  };

  const tierConfig = tier ? MYSTERY_TIERS[tier] : null;

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-bold ${sizeClasses[size]} bg-amber-100 text-amber-700`}
    >
      <img src={UNIFIED_RUMIO_CONFIG.iconUrl} alt="" className="w-4 h-4" />
      <span>{count}</span>
      {showCost && tierConfig && (
        <span className="text-gray-400 ml-1">
          {t('rumio.forTier', 'for')} {tierConfig.icon}
        </span>
      )}
    </span>
  );
}

/**
 * Tier cost indicator - shows how many Rumios needed for a tier
 */
export function TierCostBadge({
  tier,
  cost,
  userBalance,
  size = 'md',
}: {
  tier: MysteryTier;
  cost: number;
  userBalance?: number;
  size?: 'sm' | 'md' | 'lg';
}) {
  const tierConfig = MYSTERY_TIERS[tier];
  const canAfford = userBalance !== undefined && userBalance >= cost;

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5',
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-medium ${sizeClasses[size]} ${
        canAfford
          ? 'bg-green-100 text-green-700'
          : 'bg-gray-100 text-gray-600'
      }`}
    >
      <span>{tierConfig.icon}</span>
      <span>{cost}</span>
      <img src={UNIFIED_RUMIO_CONFIG.iconUrl} alt="" className="w-4 h-4" />
    </span>
  );
}
